import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { NAV_ROUTES } from '../../constants/navRoutes';
import { INVITATION_STATUS_CODE } from '../../constants/constants';
import { useLazyCheckInvitationValidityQuery } from '../../api/linkValidity';
import FullPageLoader from '../FullPageLoader';

const INVITATION_ID_PARAM = 'invitationId';

interface LinkValidityGuardProps {
  children?: ReactNode;
}

const LinkValidityGuard: FC<LinkValidityGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const invitationId = searchParams.get(INVITATION_ID_PARAM);
  const [isChecking, setIsChecking] = useState(Boolean(invitationId));
  const [checkInvitationValidity] = useLazyCheckInvitationValidityQuery();

  useEffect(() => {
    if (!invitationId) {
      setIsChecking(false);
      return;
    }

    let isStaleCheck = false;

    const proceedToDashboard = () => {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(INVITATION_ID_PARAM);
      setSearchParams(newParams, { replace: true });
      setIsChecking(false);
    };

    const verifyLink = async () => {
      try {
        const response = await checkInvitationValidity({ invitationId }).unwrap();
        if (isStaleCheck) return;

        const statusCode = response?.data?.statusCode;

        if (statusCode === INVITATION_STATUS_CODE.ACTIVE) {
          proceedToDashboard();
          return;
        }

        navigate(
          statusCode === INVITATION_STATUS_CODE.EXPIRED
            ? NAV_ROUTES.ACCESS_INVITATION_EXPIRED
            : NAV_ROUTES.ACCESS_INVITATION_INVALID,
          { replace: true },
        );
      } catch {
        // Fail open: don't block a good link behind a flaky validity check.
        if (isStaleCheck) return;
        proceedToDashboard();
      }
    };

    verifyLink();

    return () => {
      isStaleCheck = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkInvitationValidity identity is unstable
  }, [invitationId]);

  if (isChecking) {
    return <FullPageLoader />;
  }

  return <>{children}</>;
};

export default LinkValidityGuard;
